import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { getClassesQuery } from '@pm2/api';
import { useModalContentManager } from '@provider/notifier';
import { useUserIdentityState } from '@provider/user-identity';
import { EditInput, Select, Button, Tooltip } from '@ui/kit';
import { useSubjects } from '@utils/constants';
import { useCreateTeamMutation } from './api.create-team.mutation.js';
import { useUpdateTeamMutation } from './api.update-team.mutation.js';
import { wrapper, input, selectsWrapper, select, selectDropdown, buttonsWrapper, button, errorMessage } from './modal.create-team.style.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { getComponentEntriesQuery } from '../../component-entries/api/query.get-component-entries.js';
import { componentEntryStore } from '../../component-entries/state.js';

const makeTeamModalConfig = props => ({
  theme: "dialog",
  content: /*#__PURE__*/jsx(CreateTeamOverlay, {
    ...props
  })
});
function CreateTeamOverlay(props) {
  const modal = useModalContentManager();
  const queryClient = useQueryClient();
  const user = useUserIdentityState(state => state.user);
  const subjects = useSubjects();
  const [teamTitle, setTeamTitle] = useState(props.initialTeam?.title ?? "");
  const [selectedClasses, setSelectedClasses] = useState(props.initialTeam?.teams ?? []);
  const [selectedSubjects, setSelectedSubjects] = useState(props.initialTeam?.subjects ?? []);
  const classes = useQuery(getClassesQuery.useQueryConfig());
  const createTeam = useCreateTeamMutation();
  const updateTeam = useUpdateTeamMutation();
  const disabledMessage = (() => {
    const errors = [];
    if (!teamTitle) {
      errors.push("Titel skal udfyldes");
    }
    if (selectedClasses.length === 0) {
      errors.push("Mindst en klasse skal vælges");
    }
    if (selectedSubjects.length === 0) {
      errors.push("Mindst et fag skal vælges");
    }
    return errors.join("\n");
  })();
  const onSubmit = async evt => {
    evt.preventDefault();
    if (disabledMessage.length !== 0) {
      return;
    }
    modal.onDismiss();
    if (props.initialTeam) {
      await updateTeam.mutateAsync({
        ...props.initialTeam,
        title: teamTitle,
        subjects: selectedSubjects,
        teams: selectedClasses
      });
    } else {
      await createTeam.mutateAsync({
        createdAt: new Date(),
        modifiedAt: new Date(),
        enabledNotifications: true,
        key: `${teamTitle}-${selectedClasses.join(",")}-${selectedSubjects.join(",")}`,
        title: teamTitle,
        subjects: selectedSubjects,
        teams: selectedClasses
      });
    }

    // Ensure we refetch all notifications after creating a new team as they
    // may trigger additional notifcations
    queryClient.refetchQueries(getComponentEntriesQuery(user, componentEntryStore.getState().filters.timespan));
  };
  const onCancel = evt => {
    modal.onDismiss();
    evt.preventDefault();
  };
  const handleClassChange = value => {
    setSelectedClasses(value.sort((a, b) => a.localeCompare(b, "da-DK", {
      numeric: true
    })));
  };
  const handleSubjectChange = value => {
    setSelectedSubjects(value.sort((a, b) => a.localeCompare(b, "da-DK", {
      numeric: true
    })));
  };
  return /*#__PURE__*/jsxs("form", {
    className: wrapper,
    onSubmit: onSubmit,
    noValidate: true,
    children: [/*#__PURE__*/jsx("div", {
      className: input,
      children: /*#__PURE__*/jsx(EditInput, {
        mode: "always-visible",
        required: true,
        type: "text",
        value: teamTitle,
        placeholder: "Titel p\xE5 hold",
        "aria-label": "Angiv titel p\xE5 hold",
        onChange: setTeamTitle
      })
    }), /*#__PURE__*/jsxs("div", {
      className: selectsWrapper,
      children: [/*#__PURE__*/jsx("div", {
        className: select,
        children: /*#__PURE__*/jsx(Select, {
          dropdownClassName: selectDropdown,
          label: "Klasse",
          onChange: handleClassChange,
          selectedValues: selectedClasses,
          multiSelect: true,
          options: classes.data ? classes.data.map(c => c.name) : []
        })
      }), /*#__PURE__*/jsx("div", {
        className: select,
        children: /*#__PURE__*/jsx(Select, {
          dropdownClassName: selectDropdown,
          label: "Fag",
          onChange: handleSubjectChange,
          selectedValues: selectedSubjects,
          multiSelect: true,
          options: subjects
        })
      })]
    }), /*#__PURE__*/jsxs("div", {
      className: buttonsWrapper,
      children: [/*#__PURE__*/jsx(Button, {
        onClick: onCancel,
        className: button,
        theme: "secondary",
        children: "Annuller"
      }), /*#__PURE__*/jsx(Tooltip, {
        placement: "top",
        body: disabledMessage,
        ariaType: "description",
        className: errorMessage,
        disabled: disabledMessage.length === 0,
        children: ref => /*#__PURE__*/jsx(Button, {
          ref: ref,
          disabled: disabledMessage.length !== 0,
          "aria-disabled": disabledMessage.length !== 0,
          className: button,
          theme: "primary",
          type: "submit",
          children: "Gem"
        })
      })]
    })]
  });
}

export { CreateTeamOverlay, makeTeamModalConfig };
//# sourceMappingURL=modal.create-team.js.map
